import { z } from "zod";
import { templateSchema } from "./catalog.js";
import { consentReceiptSchema, generatedAssetSchema, generationJobSchema, narrationAssetSchema, storyBriefSchema, storyProjectSchema, storyRevisionSchema, workflowKindSchema, } from "./story.js";
export const ulidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/i, "Expected an opaque ULID");
export const emailSchema = z.string().email().max(254);
export const reminderCadenceSchema = z.enum(["WEEKLY", "BIWEEKLY", "PAUSED"]);
export const reminderTargetRoleSchema = z.enum(["OWNER", "EDITOR", "CONTRIBUTOR"]);
export const bookRoleSchema = z.enum(["OWNER", "EDITOR", "CONTRIBUTOR", "VIEWER"]);
export const bookStateSchema = z.enum([
    "SETUP",
    "COLLECTING",
    "READY_FOR_REVIEW",
    "APPROVED",
    "RENDERING",
    "PREFLIGHT_FAILED",
    "FULFILLING",
    "COMPLETED",
    "ARCHIVED",
]);
export const chapterStateSchema = z.enum([
    "DRAFT",
    "TRANSCRIBING",
    "GENERATING",
    "READY_FOR_REVIEW",
    "APPROVED",
    "FAILED",
]);
export const mediaAssetStateSchema = z.enum([
    "UPLOADING",
    "QUARANTINED",
    "AVAILABLE",
    "NEEDS_ATTENTION",
    "REJECTED",
    "DELETING",
    "DELETED",
]);
export const mediaValidationStateSchema = z.enum(["PENDING", "PASSED", "FAILED"]);
export const malwareScanStateSchema = z.enum([
    "PENDING",
    "NO_THREATS_FOUND",
    "THREATS_FOUND",
    "UNSUPPORTED",
    "ACCESS_DENIED",
    "FAILED",
]);
export const fulfillmentStateSchema = z.enum([
    "PENDING",
    "SUBMITTING",
    "SANDBOX_SUBMITTED",
    "IN_PRODUCTION",
    "SHIPPED",
    "DELIVERED",
    "NEEDS_ATTENTION",
    "CANCELLED",
]);
export const checkoutStateSchema = z.enum([
    "PENDING",
    "PAID",
    "EXPIRED",
    "PARTIALLY_REFUNDED",
    "REFUNDED",
    "DISPUTED",
]);
export const templateSnapshotSchema = templateSchema.extend({
    snapshotAt: z.string().datetime(),
});
export const CREATIVE_MEDIA_POLICY_VERSION = "2026-09-22.1";
export const creativeMediaDecisionSchema = z
    .object({
    policyVersion: z.literal(CREATIVE_MEDIA_POLICY_VERSION),
    decision: z.enum(["ACCEPTED", "DECLINED"]),
    decidedAt: z.string().datetime(),
})
    .strict();
export const userProfileSchema = z.object({
    id: ulidSchema,
    email: emailSchema,
    displayName: z.string().min(1).max(100),
    reminderCadence: reminderCadenceSchema,
    creativeMediaDecision: creativeMediaDecisionSchema.optional(),
    createdAt: z.string().datetime(),
});
export const updateProfileCommandSchema = z
    .object({
    reminderCadence: reminderCadenceSchema.optional(),
    creativeMediaDecision: z.enum(["ACCEPTED", "DECLINED"]).optional(),
})
    .strict()
    .refine((command) => command.reminderCadence !== undefined || command.creativeMediaDecision !== undefined, "Choose at least one profile preference to update");
export const reminderPolicySchema = z.object({
    bookId: ulidSchema,
    targetRoles: z
        .array(reminderTargetRoleSchema)
        .min(1)
        .max(3)
        .refine((roles) => roles.includes("OWNER"), "The book owner must remain a reminder target")
        .refine((roles) => new Set(roles).size === roles.length, "Reminder target roles must be unique"),
    updatedAt: z.string().datetime(),
});
export const updateReminderPolicyCommandSchema = z
    .object({
    targetRoles: reminderPolicySchema.shape.targetRoles,
})
    .strict();
export const unsubscribeReminderCommandSchema = z
    .object({
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/, "Expected an opaque unsubscribe token"),
})
    .strict();
export const bookMemberSchema = z.object({
    userId: ulidSchema,
    bookId: ulidSchema,
    role: bookRoleSchema,
    joinedAt: z.string().datetime(),
});
export const bookSchema = z.object({
    id: ulidSchema,
    ownerId: ulidSchema,
    title: z.string().min(1).max(160),
    storytellerName: z.string().min(1).max(100),
    state: bookStateSchema,
    revision: z.number().int().nonnegative(),
    workflowKind: workflowKindSchema.default("INTERVIEW"),
    template: templateSnapshotSchema,
    test: z.boolean(),
    reminderTargetRoles: reminderPolicySchema.shape.targetRoles.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const promptSchema = z.object({
    id: ulidSchema,
    bookId: ulidSchema,
    position: z.number().int().nonnegative(),
    question: z.string().min(1).max(1000),
    assignedTo: ulidSchema.optional(),
});
export const chapterRevisionOriginSchema = z.enum([
    "TRANSCRIPTION",
    "MANUAL_EDIT",
    "AI_GENERATION",
    "REVERT",
]);
export const chapterRevisionReviewStatusSchema = z.enum(["PENDING", "APPROVED"]);
export const claimSupportStatusSchema = z.enum(["SUPPORTED", "CHANGED", "UNSUPPORTED"]);
export const transcriptEvidenceSchema = z
    .object({
    start: z.number().int().nonnegative(),
    end: z.number().int().positive(),
    quote: z.string().min(1).max(10_000),
})
    .refine((evidence) => evidence.end > evidence.start, {
    message: "Evidence must end after it starts.",
    path: ["end"],
});
export const chapterSourceFactSchema = z.object({
    text: z.string().trim().min(1).max(10_000),
    status: claimSupportStatusSchema,
    evidence: z.array(transcriptEvidenceSchema).max(20),
});
export const chapterDraftClaimSchema = z.object({
    text: z.string().trim().min(1).max(10_000),
    start: z.number().int().nonnegative(),
    end: z.number().int().positive(),
    status: claimSupportStatusSchema,
    sourceFactIndexes: z.array(z.number().int().nonnegative()).max(100),
    evidence: z.array(transcriptEvidenceSchema).max(20),
});
export const chapterGenerationProvenanceSchema = z
    .object({
    provider: z.literal("AMAZON_BEDROCK"),
    modelId: z.string().min(1).max(500),
    modelVersion: z.string().min(1).max(200),
    inferenceProfileId: z.string().min(1).max(500),
    modelPolicyStatus: z.enum(["PROVISIONAL", "GATE_PASSED"]),
    promptProfileId: z.string().min(1).max(100),
    promptVersion: z.string().min(1).max(100),
    promptHashSha256: z.string().regex(/^[a-f0-9]{64}$/i),
    outputSchemaName: z.string().min(1).max(100),
    outputSchemaVersion: z.string().min(1).max(100),
    outputSchemaHashSha256: z.string().regex(/^[a-f0-9]{64}$/i),
    guardrail: z
        .object({
        identifier: z.string().min(1).max(500),
        version: z.string().min(1).max(100),
    })
        .strict()
        .nullable(),
    generationSettings: z
        .object({
        maxTokens: z.number().int().positive().max(100_000),
        temperature: z.number().min(0).max(1).nullable(),
        topP: z.number().min(0).max(1).nullable(),
        stopSequences: z.array(z.string().max(1_000)).max(20),
        additionalModelRequestFields: z.record(z.string(), z.json()).optional(),
    })
        .strict(),
    generatedAt: z.string().datetime(),
})
    .strict();
export const chapterRevisionSchema = z
    .object({
    id: ulidSchema,
    bookId: ulidSchema,
    chapterId: ulidSchema,
    revision: z.number().int().positive(),
    parentRevision: z.number().int().nonnegative(),
    origin: chapterRevisionOriginSchema,
    reviewStatus: chapterRevisionReviewStatusSchema,
    title: z.string().min(1).max(200),
    rawTranscript: z.string().max(100_000),
    draft: z.string().max(100_000),
    sourceAssetIds: z.array(ulidSchema),
    generation: chapterGenerationProvenanceSchema.optional(),
    sourceFacts: z.array(chapterSourceFactSchema).max(1_000),
    draftClaims: z.array(chapterDraftClaimSchema).max(1_000),
    revertedFromRevision: z.number().int().positive().optional(),
    createdBy: ulidSchema,
    createdAt: z.string().datetime(),
    approvedBy: ulidSchema.optional(),
    approvedAt: z.string().datetime().optional(),
    /**
     * The family's own details its approval counted as source (2026-10-16): the book's title,
     * storyteller and dedication, and the chapter title they approved. Kept with the approval, so
     * printing or exporting the chapter later checks it against what the family saw then.
     */
    evidenceContext: z.array(z.string().max(5_000)).max(8).optional(),
})
    .superRefine((revision, context) => {
    if (revision.origin === "AI_GENERATION" && !revision.generation)
        context.addIssue({
            code: "custom",
            path: ["generation"],
            message: "AI-generated revisions require exact generation provenance.",
        });
    revision.sourceFacts.forEach((fact, factIndex) => {
        fact.evidence.forEach((evidence, evidenceIndex) => {
            if (revision.rawTranscript.slice(evidence.start, evidence.end) !== evidence.quote)
                context.addIssue({
                    code: "custom",
                    path: ["sourceFacts", factIndex, "evidence", evidenceIndex],
                    message: "Source-fact evidence must match the transcript span exactly.",
                });
        });
    });
    revision.draftClaims.forEach((claim, claimIndex) => {
        if (revision.draft.slice(claim.start, claim.end) !== claim.text)
            context.addIssue({
                code: "custom",
                path: ["draftClaims", claimIndex],
                message: "Draft claim text must match the draft span exactly.",
            });
        claim.sourceFactIndexes.forEach((sourceFactIndex, indexPosition) => {
            if (sourceFactIndex >= revision.sourceFacts.length)
                context.addIssue({
                    code: "custom",
                    path: ["draftClaims", claimIndex, "sourceFactIndexes", indexPosition],
                    message: "Draft claims may reference only source facts in the same revision.",
                });
        });
        claim.evidence.forEach((evidence, evidenceIndex) => {
            if (revision.rawTranscript.slice(evidence.start, evidence.end) !== evidence.quote)
                context.addIssue({
                    code: "custom",
                    path: ["draftClaims", claimIndex, "evidence", evidenceIndex],
                    message: "Draft-claim evidence must match the transcript span exactly.",
                });
        });
    });
});
export const chapterSchema = z.object({
    id: ulidSchema,
    bookId: ulidSchema,
    promptId: ulidSchema,
    title: z.string().min(1).max(200),
    state: chapterStateSchema,
    currentRevision: z.number().int().nonnegative(),
    latestRevision: z.number().int().nonnegative(),
    activeRevision: z.number().int().nonnegative(),
    rawTranscript: z.string().max(100_000),
    draft: z.string().max(100_000),
    activeRawTranscript: z.string().max(100_000),
    activeDraft: z.string().max(100_000),
    updatedAt: z.string().datetime(),
});
export const mediaAssetSchema = z.object({
    id: ulidSchema,
    bookId: ulidSchema,
    ownerId: ulidSchema,
    kind: z.enum(["PHOTO", "AUDIO", "VIDEO", "ART", "PREVIEW", "PRINT"]),
    status: mediaAssetStateSchema,
    contentType: z.string().max(200),
    byteSize: z.number().int().nonnegative(),
    checksumSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/i)
        .optional(),
    validationStatus: mediaValidationStateSchema.optional(),
    malwareScanStatus: malwareScanStateSchema.optional(),
    validationFailureReason: z
        .enum(["SIGNATURE_MISMATCH", "CHECKSUM_MISMATCH", "SIZE_MISMATCH"])
        .optional(),
    validatedAt: z.string().datetime().optional(),
    malwareScannedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
});
export const opaqueShareTokenSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{43}$/, "Expected an opaque media share token");
export const mediaShareStateSchema = z.enum(["ENABLED", "REVOKED"]);
export const mediaShareSchema = z
    .object({
    id: ulidSchema,
    bookId: ulidSchema,
    assetId: ulidSchema,
    chapterId: ulidSchema.optional(),
    state: mediaShareStateSchema,
    url: z.string().url().max(2_048),
    createdBy: ulidSchema,
    createdAt: z.string().datetime(),
    revokedBy: ulidSchema.optional(),
    revokedAt: z.string().datetime().optional(),
})
    .strict();
export const voucherSchema = z.object({
    id: ulidSchema,
    checkoutId: ulidSchema,
    orderId: ulidSchema.optional(),
    templateId: z.string(),
    codeHash: z.string().min(32),
    status: z.enum(["ISSUED", "SUSPENDED", "REDEEMED", "REVOKED"]),
    test: z.boolean(),
    issuedAt: z.string().datetime(),
    redeemedAt: z.string().datetime().optional(),
});
export const checkoutSchema = z.object({
    id: ulidSchema,
    purchaserEmail: emailSchema,
    templateId: z.string(),
    amountCents: z.number().int().positive(),
    state: checkoutStateSchema,
    stripeSessionId: z.string().optional(),
    stripeSessionUrl: z.string().url().optional(),
    test: z.boolean(),
    createdAt: z.string().datetime(),
});
export const orderSchema = z.object({
    id: ulidSchema,
    checkoutId: ulidSchema,
    bookId: ulidSchema.optional(),
    fulfillmentState: fulfillmentStateSchema,
    amountCents: z.number().int().nonnegative(),
    test: z.boolean(),
    createdAt: z.string().datetime(),
});
export const pointsLedgerEntrySchema = z.object({
    id: ulidSchema,
    userId: ulidSchema,
    delta: z.number().int(),
    reason: z.enum([
        "PURCHASE",
        "REFUND",
        "DISPUTE",
        "DISPUTE_REVERSAL",
        "REDEMPTION",
        "REDEMPTION_REVERSAL",
        "ADJUSTMENT",
    ]),
    sourceId: ulidSchema,
    test: z.boolean(),
    createdAt: z.string().datetime(),
});
export const rewardProductSchema = z
    .object({
    id: z.string().regex(/^[a-z][a-z0-9-]+$/),
    name: z.string().min(2).max(100),
    pointsCost: z.number().int().positive().optional(),
    cashPriceCents: z.number().int().positive().optional(),
    fulfillment: z.enum(["GELATO_DRAFT", "VOUCHER"]),
    active: z.boolean(),
})
    .refine((reward) => reward.pointsCost !== undefined || reward.cashPriceCents !== undefined, "A reward must have a points cost, cash price, or both.");
export const rewardRedemptionStateSchema = z.enum([
    "QUEUED",
    "FULFILLED",
    "NEEDS_ATTENTION",
    "CANCELLED",
]);
export const rewardFulfillmentStateSchema = z.enum([
    "TEST_GATE_BLOCKED",
    "GELATO_DRAFT_CREATED",
    "VOUCHER_ISSUED",
    "NEEDS_ATTENTION",
    "CANCELLED",
]);
export const rewardRedemptionSchema = z.object({
    id: ulidSchema,
    userId: ulidSchema,
    rewardId: rewardProductSchema.shape.id,
    pointsSpent: z.number().int().positive(),
    state: rewardRedemptionStateSchema,
    fulfillmentState: rewardFulfillmentStateSchema,
    vendorSubmissionEnabled: z.literal(false),
    test: z.literal(true),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const rewardCashOrderSchema = z.object({
    id: ulidSchema,
    checkoutId: ulidSchema,
    userId: ulidSchema,
    rewardId: rewardProductSchema.shape.id,
    rewardName: rewardProductSchema.shape.name,
    amountCents: z.number().int().positive(),
    fulfillmentState: rewardFulfillmentStateSchema,
    vendorSubmissionEnabled: z.literal(false),
    pointsEligible: z.literal(false),
    paymentSource: z.literal("STRIPE_TEST"),
    test: z.literal(true),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const rewardOrderSummarySchema = z
    .object({
    id: ulidSchema,
    checkoutId: ulidSchema,
    rewardId: rewardProductSchema.shape.id,
    rewardName: rewardProductSchema.shape.name,
    amountCents: z.number().int().positive(),
    paymentState: checkoutStateSchema,
    fulfillmentState: rewardFulfillmentStateSchema,
    vendorSubmissionEnabled: z.literal(false),
    pointsEligible: z.literal(false),
    test: z.literal(true),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const rewardOrderListResponseSchema = z
    .object({
    data: z.array(rewardOrderSummarySchema).max(50),
    meta: z
        .object({
        count: z.number().int().nonnegative().max(50),
        limit: z.literal(50),
        hasMore: z.boolean(),
        test: z.literal(true),
    })
        .strict(),
})
    .strict();
export const rewardCatalogResponseSchema = z.object({
    data: z.array(rewardProductSchema),
    meta: z.object({ count: z.number().int().nonnegative(), test: z.literal(true) }),
});
export const rewardRedemptionResponseSchema = z.object({
    data: z.object({
        redemption: rewardRedemptionSchema,
        remainingBalance: z.number().int().nonnegative(),
    }),
    message: z.string().min(1),
});
export const pointsRecoveryStateSchema = z.enum(["NEEDS_ATTENTION", "RESOLVED", "SUPERSEDED"]);
export const pointsRecoverySchema = z.object({
    id: ulidSchema,
    checkoutId: ulidSchema,
    userId: ulidSchema,
    stripeEventId: z.string().min(1).max(255),
    stripeEventType: z.string().min(1).max(100),
    financialReason: z.enum(["REFUND", "DISPUTE"]),
    priorPointsIssued: z.number().int().nonnegative(),
    desiredPoints: z.number().int().nonnegative(),
    entitlementDelta: z.number().int(),
    pointsApplied: z.number().int().nonnegative(),
    pointsShortfall: z.number().int().positive(),
    balanceBefore: z.number().int().nonnegative(),
    balanceAfter: z.number().int().nonnegative(),
    state: pointsRecoveryStateSchema,
    test: z.literal(true),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const resolvePointsRecoveryCommandSchema = z
    .object({
    redemptionIds: z
        .array(ulidSchema)
        .max(10)
        .refine((ids) => new Set(ids).size === ids.length, "Redemption identifiers must be unique"),
})
    .strict();
export const pointsRecoveryResolutionResponseSchema = z.object({
    data: z.object({
        recoveryId: ulidSchema,
        state: z.literal("RESOLVED"),
        finalBalance: z.number().int().nonnegative(),
        cancelledRedemptionIds: z.array(ulidSchema),
        test: z.literal(true),
    }),
    message: z.string().min(1),
});
export const supportGrantStateSchema = z.enum(["PENDING_OWNER", "ACTIVE", "REVOKED", "EXPIRED"]);
export const supportGrantDeliveryStateSchema = z.enum(["PENDING", "SENT", "NEEDS_ATTENTION"]);
export const supportGrantReasonSchema = z.literal("BOOK_WORKSPACE_SUPPORT");
export const supportGrantSchema = z.object({
    id: ulidSchema,
    bookId: ulidSchema,
    ownerId: ulidSchema,
    operatorId: z.string().min(1).max(128),
    state: supportGrantStateSchema,
    deliveryState: supportGrantDeliveryStateSchema,
    reason: supportGrantReasonSchema,
    accessMode: z.literal("VIEW_ONLY"),
    rawMediaPlaybackEnabled: z.literal(false),
    requestedAt: z.string().datetime(),
    approvalExpiresAt: z.string().datetime(),
    stateExpiresAtEpoch: z.number().int().positive(),
    approvedAt: z.string().datetime().optional(),
    activeUntil: z.string().datetime().optional(),
    revokedAt: z.string().datetime().optional(),
    expiredAt: z.string().datetime().optional(),
    test: z.literal(true),
    updatedAt: z.string().datetime(),
});
export const requestSupportGrantCommandSchema = z
    .object({
    bookId: ulidSchema,
    reason: supportGrantReasonSchema,
})
    .strict();
export const approveSupportGrantCommandSchema = z
    .object({
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/, "Expected an opaque approval token"),
})
    .strict();
export const revokeSupportGrantCommandSchema = z.object({}).strict();
export const supportGrantPublicSchema = supportGrantSchema.pick({
    id: true,
    bookId: true,
    state: true,
    deliveryState: true,
    reason: true,
    accessMode: true,
    rawMediaPlaybackEnabled: true,
    requestedAt: true,
    approvalExpiresAt: true,
    approvedAt: true,
    activeUntil: true,
    revokedAt: true,
    expiredAt: true,
    test: true,
});
export const supportGrantResponseSchema = z.object({
    data: z.object({ grant: supportGrantPublicSchema }),
    message: z.string().min(1),
});
export const supportChapterDiagnosticSchema = z
    .object({
    id: ulidSchema,
    promptId: ulidSchema,
    position: z.number().int().nonnegative().nullable(),
    title: z.string().min(1).max(200),
    state: chapterStateSchema,
    currentRevision: z.number().int().nonnegative(),
    latestRevision: z.number().int().nonnegative(),
    activeRevision: z.number().int().nonnegative(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const supportMediaDiagnosticSchema = z
    .object({
    id: ulidSchema,
    kind: mediaAssetSchema.shape.kind,
    status: mediaAssetStateSchema,
    byteSize: z.number().int().nonnegative(),
    validationStatus: mediaValidationStateSchema.nullable(),
    malwareScanStatus: malwareScanStateSchema.nullable(),
    createdAt: z.string().datetime(),
})
    .strict();
export const supportMemberCountsSchema = z
    .object({
    OWNER: z.number().int().nonnegative(),
    EDITOR: z.number().int().nonnegative(),
    CONTRIBUTOR: z.number().int().nonnegative(),
    VIEWER: z.number().int().nonnegative(),
})
    .strict();
export const supportViewResponseSchema = z
    .object({
    data: z
        .object({
        grant: supportGrantPublicSchema,
        book: z
            .object({
            id: ulidSchema,
            title: z.string().min(1).max(160),
            storytellerName: z.string().min(1).max(100),
            state: bookStateSchema,
            revision: z.number().int().nonnegative(),
            template: z
                .object({ id: z.string().min(1), version: z.number().int().positive() })
                .strict(),
            updatedAt: z.string().datetime(),
        })
            .strict(),
        chapters: z.array(supportChapterDiagnosticSchema).max(100),
        media: z.array(supportMediaDiagnosticSchema).max(50),
        memberCounts: supportMemberCountsSchema,
    })
        .strict(),
    message: z.string().min(1),
})
    .strict();
export const bookExportStateSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "READY",
    "NEEDS_ATTENTION",
    "EXPIRED",
]);
export const requestBookExportCommandSchema = z.object({}).strict();
export const bookExportSchema = z
    .object({
    id: ulidSchema,
    bookId: ulidSchema,
    state: bookExportStateSchema,
    format: z.literal("JSON"),
    requestedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    readyAt: z.string().datetime().optional(),
    expiresAt: z.string().datetime().optional(),
    byteSize: z
        .number()
        .int()
        .positive()
        .max(500 * 1024 * 1024)
        .optional(),
    checksumSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/i)
        .optional(),
    test: z.literal(true),
})
    .strict();
export const bookExportResponseSchema = z
    .object({
    data: z.object({ export: bookExportSchema }).strict(),
    message: z.string().min(1).max(500),
})
    .strict();
export const bookExportDownloadResponseSchema = z
    .object({
    data: z
        .object({
        exportId: ulidSchema,
        url: z.string().url().max(8_192),
        expiresInSeconds: z.literal(300),
        byteSize: z
            .number()
            .int()
            .positive()
            .max(500 * 1024 * 1024),
        checksumSha256: z.string().regex(/^[a-f0-9]{64}$/i),
    })
        .strict(),
})
    .strict();
export const bookDeletionStateSchema = z.enum([
    "RECOVERY_WINDOW",
    "CANCELLED",
    "PURGING",
    "PURGED",
    "NEEDS_ATTENTION",
]);
export const requestBookDeletionCommandSchema = z
    .object({ confirmationTitle: z.string().min(1).max(160) })
    .strict();
export const cancelBookDeletionCommandSchema = z.object({}).strict();
export const bookDeletionSchema = z
    .object({
    id: ulidSchema,
    bookId: ulidSchema,
    state: bookDeletionStateSchema,
    priorBookState: bookStateSchema,
    sharesRevoked: z.number().int().nonnegative().max(100),
    requestedAt: z.string().datetime(),
    scheduledFor: z.string().datetime(),
    updatedAt: z.string().datetime(),
    cancelledAt: z.string().datetime().optional(),
    purgedAt: z.string().datetime().optional(),
    test: z.literal(true),
})
    .strict();
export const bookDeletionResponseSchema = z
    .object({
    data: z.object({ deletion: bookDeletionSchema }).strict(),
    message: z.string().min(1).max(500),
})
    .strict();
export const workflowJobSchema = z.object({
    id: ulidSchema,
    kind: z.enum(["TRANSCRIPTION", "GENERATION", "RENDER", "FULFILLMENT", "REWARD"]),
    subjectId: ulidSchema,
    state: z.enum(["QUEUED", "RUNNING", "SUCCEEDED", "FAILED", "NEEDS_ATTENTION"]),
    attempt: z.number().int().nonnegative(),
    correlationId: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const bookPreviewStateSchema = z.enum([
    "QUEUED",
    "RENDERING",
    "PREFLIGHTING",
    "READY",
    "PREFLIGHT_FAILED",
    "NEEDS_ATTENTION",
    "STALE",
    "APPROVED",
]);
export const printArtifactKindSchema = z.enum(["INTERIOR", "COVER"]);
export const printPreflightIssueSchema = z.object({
    code: z.string().min(1).max(100),
    message: z.string().min(1).max(500),
    artifact: printArtifactKindSchema.optional(),
});
export const printArtifactSchema = z.object({
    kind: printArtifactKindSchema,
    contentType: z.literal("application/pdf"),
    byteSize: z
        .number()
        .int()
        .positive()
        .max(500 * 1024 * 1024),
    checksumSha256: z.string().regex(/^[a-f0-9]{64}$/i),
    pageCount: z.number().int().positive(),
    widthPoints: z.number().positive(),
    heightPoints: z.number().positive(),
    createdAt: z.string().datetime(),
});
export const bookPreviewSchema = z.object({
    id: ulidSchema,
    bookId: ulidSchema,
    sourceRevision: z.number().int().nonnegative(),
    profileId: z.string().min(1).max(200),
    specificationVersion: z.string().min(1).max(100),
    state: bookPreviewStateSchema,
    preflightPassed: z.boolean(),
    issues: z.array(printPreflightIssueSchema).max(100),
    artifacts: z.array(printArtifactSchema).max(2),
    fulfillmentQueued: z.boolean(),
    vendorSubmissionEnabled: z.literal(false),
    test: z.literal(true),
    createdBy: ulidSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    approvedBy: ulidSchema.optional(),
    approvedAt: z.string().datetime().optional(),
});
const exportedBookSchema = bookSchema
    .pick({
    id: true,
    title: true,
    storytellerName: true,
    state: true,
    revision: true,
    workflowKind: true,
    template: true,
    test: true,
    reminderTargetRoles: true,
    createdAt: true,
    updatedAt: true,
})
    .strict();
const exportedPromptSchema = promptSchema
    .pick({ id: true, bookId: true, position: true, question: true })
    .strict();
const exportedChapterSchema = chapterSchema
    .pick({
    id: true,
    bookId: true,
    promptId: true,
    title: true,
    state: true,
    currentRevision: true,
    latestRevision: true,
    activeRevision: true,
    rawTranscript: true,
    draft: true,
    activeRawTranscript: true,
    activeDraft: true,
    updatedAt: true,
})
    .strict();
const exportedRevisionSchema = chapterRevisionSchema
    .omit({ createdBy: true, approvedBy: true })
    .strict();
const exportedMediaSchema = mediaAssetSchema
    .omit({ ownerId: true })
    .extend({
    chapterId: ulidSchema.optional(),
    transcriptionState: z.string().max(100).optional(),
    transcriptionUpdatedAt: z.string().datetime().optional(),
})
    .strict();
const exportedShareSchema = z
    .object({
    id: ulidSchema,
    bookId: ulidSchema,
    assetId: ulidSchema,
    chapterId: ulidSchema.optional(),
    state: mediaShareStateSchema,
    createdAt: z.string().datetime(),
    revokedAt: z.string().datetime().optional(),
})
    .strict();
const exportedPreviewSchema = bookPreviewSchema
    .pick({
    id: true,
    bookId: true,
    sourceRevision: true,
    profileId: true,
    specificationVersion: true,
    state: true,
    preflightPassed: true,
    issues: true,
    artifacts: true,
    fulfillmentQueued: true,
    vendorSubmissionEnabled: true,
    test: true,
    createdAt: true,
    updatedAt: true,
    approvedAt: true,
})
    .strict();
const exportedStoryRevisionSchema = storyRevisionSchema
    .omit({ createdBy: true, approvedBy: true })
    .strict();
const exportedStorySchema = z
    .object({
    project: storyProjectSchema.optional(),
    brief: storyBriefSchema.optional(),
    revisions: z.array(exportedStoryRevisionSchema).max(100),
    jobs: z.array(generationJobSchema).max(100),
    generatedAssets: z.array(generatedAssetSchema).max(100),
    consents: z.array(consentReceiptSchema).max(20),
    narrations: z.array(narrationAssetSchema).max(20),
    sourceTranscripts: z
        .array(z
        .object({
        assetId: ulidSchema,
        transcript: z.string().min(1).max(100_000),
        provider: z.enum(["ELEVENLABS", "AMAZON_TRANSCRIBE", "CUSTOMER_TEXT"]),
        createdAt: z.string().datetime(),
    })
        .strict())
        .max(10),
    legacySource: z.json().optional(),
})
    .strict();
export const bookExportDocumentSchema = z
    .object({
    schemaVersion: z.literal("2026-08-28.1"),
    exportedAt: z.string().datetime(),
    book: exportedBookSchema,
    prompts: z.array(exportedPromptSchema).max(250),
    chapters: z.array(exportedChapterSchema).max(250),
    revisions: z.array(exportedRevisionSchema).max(2_000),
    media: z.array(exportedMediaSchema).max(250),
    shares: z.array(exportedShareSchema).max(100),
    previews: z.array(exportedPreviewSchema).max(100),
    story: exportedStorySchema.optional(),
    recipeSourceTranscripts: z
        .array(z
        .object({
        recipeId: z.string().min(1).max(160),
        jobId: ulidSchema,
        assetId: ulidSchema,
        sourceRecipeRevision: z.number().int().positive(),
        transcript: z.string().min(1).max(100000),
        provider: z.enum(["ELEVENLABS", "AMAZON_TRANSCRIBE"]),
        createdAt: z.string().datetime(),
        confirmedRevision: z.number().int().positive().optional(),
    })
        .strict())
        .max(2000)
        .optional(),
})
    .strict();
export const createCheckoutCommandSchema = z
    .object({
    productId: z.literal("book-voucher"),
    purchaserEmail: z.string().trim().email().max(254),
    gift: z.boolean().default(false),
    recipientEmail: z.string().trim().email().max(254).optional(),
})
    .strict()
    .superRefine((command, context) => {
    if (command.gift && !command.recipientEmail)
        context.addIssue({
            code: "custom",
            path: ["recipientEmail"],
            message: "A gift voucher requires the recipient email.",
        });
    if (!command.gift && command.recipientEmail)
        context.addIssue({
            code: "custom",
            path: ["recipientEmail"],
            message: "A self-purchase cannot include a separate recipient email.",
        });
    if (command.gift &&
        command.recipientEmail?.trim().toLowerCase() === command.purchaserEmail.trim().toLowerCase())
        context.addIssue({
            code: "custom",
            path: ["recipientEmail"],
            message: "Use self-purchase when purchaser and recipient are the same.",
        });
});
export const createRewardCheckoutCommandSchema = z.object({}).strict();
export const invitationRoleSchema = z.enum(["EDITOR", "CONTRIBUTOR", "VIEWER"]);
export const createInvitationCommandSchema = z
    .object({
    email: emailSchema,
    role: invitationRoleSchema,
})
    .strict();
/**
 * Sends a pending invitation again (E11, 2026-11-02), on the same route as sending one: the email
 * carries a new link and the earlier link stops working.
 */
export const resendInvitationCommandSchema = z
    .object({ action: z.literal("RESEND"), invitationId: ulidSchema })
    .strict();
/** What the invitation route accepts: a new invitation, or sending a pending one again. */
export const invitationCommandSchema = z.union([
    createInvitationCommandSchema,
    resendInvitationCommandSchema,
]);
export const acceptInvitationCommandSchema = z.object({ token: z.string().min(20).max(128) });
/**
 * What sending or resending an invitation reports: whether its email went out. `test` is true for
 * an invitation sent from a test account, and false for a real customer's (D17, 2026-11-02).
 */
export const invitationDeliveryResponseSchema = z
    .object({
    data: z
        .object({
        invitationId: ulidSchema,
        deliveryState: z.enum(["PENDING", "SENT", "NEEDS_ATTENTION"]),
        test: z.boolean(),
    })
        .strict(),
})
    .strict();
/** Why an invitation can't be accepted or sent again, as the server names it. */
export const invitationProblemCodes = {
    /** The signed-in account's email isn't the one the invitation was sent to. */
    emailMismatch: "INVITATION_EMAIL_MISMATCH",
    /** Sent within the last few minutes: it can be sent again a little later. */
    resendTooSoon: "INVITATION_RESEND_TOO_SOON",
    /** Sent as many times as one invitation may be: withdraw it and send a new one. */
    resendLimit: "INVITATION_RESEND_LIMIT",
};
export const createUploadCommandSchema = z.object({
    contentType: z.string().min(3).max(200),
    byteSize: z.number().int().positive().max(2_147_483_648),
    checksumSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/i)
        .optional(),
});
export const refreshUploadPartsCommandSchema = z.object({
    partNumbers: z.array(z.number().int().min(1).max(10_000)).min(1).max(100),
});
export const completeUploadCommandSchema = z.object({
    uploadId: z.string().min(8).max(1000),
    parts: z
        .array(z.object({
        partNumber: z.number().int().min(1).max(10_000),
        etag: z.string().min(3).max(200),
    }))
        .min(1)
        .max(10_000),
});
export const redeemVoucherCommandSchema = z
    .object({
    challengeToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
    templateId: templateSchema.shape.id.optional(),
    storytellerName: z.string().trim().min(1).max(100).optional(),
})
    .strict();
export const createExampleBookCommandSchema = z
    .object({
    templateId: z.string().regex(/^[a-z][a-z0-9-]+$/),
    storytellerName: z.string().trim().min(1).max(100).optional(),
})
    .strict();
export const exampleBookResponseSchema = z.object({
    data: z.object({
        bookId: ulidSchema,
        firstChapterId: ulidSchema,
        title: z.string().min(1).max(160),
        state: z.literal("SETUP"),
        existing: z.boolean(),
        test: z.literal(true),
        workflowKind: workflowKindSchema.default("INTERVIEW"),
        startPath: z.string().startsWith("/app/books/").optional(),
    }),
    message: z.string(),
});
export const checkoutSessionResponseSchema = z.object({
    data: z.object({
        checkoutId: ulidSchema,
        state: checkoutStateSchema,
        checkoutUrl: z.string().url(),
        test: z.literal(true),
    }),
    message: z.string(),
});
export const voucherRedemptionResponseSchema = z.object({
    data: z.object({
        bookId: ulidSchema,
        title: z.string().min(1).max(160).optional(),
        state: z.literal("SETUP"),
        test: z.boolean(),
        templateId: templateSchema.shape.id.optional(),
        workflowKind: workflowKindSchema.optional(),
        startPath: z.string().startsWith("/app/books/").optional(),
        reusable: z.boolean().optional(),
        testGiftJourney: z.literal(true).optional(),
        paidProviderCallsAllowed: z.boolean().optional(),
        simulation: z.literal(true).optional(),
        physicalShipment: z.literal(false).optional(),
        notice: z.string().min(1).max(500).optional(),
    }),
});
export const updateChapterCommandSchema = z
    .object({
    expectedRevision: z.number().int().nonnegative(),
    rawTranscript: z.string().max(100_000),
    draft: z.string().max(100_000),
})
    .strict();
export const approveChapterRevisionCommandSchema = z
    .object({ expectedRevision: z.number().int().nonnegative() })
    .strict();
export const revertChapterRevisionCommandSchema = z
    .object({ expectedRevision: z.number().int().nonnegative() })
    .strict();
export const startTranscriptionCommandSchema = z
    .object({
    assetId: ulidSchema,
    diarize: z.boolean().default(true),
    tagAudioEvents: z.boolean().default(true),
    /** Add what is said after the memory's words, as an answer to a question about it. */
    append: z.boolean().optional(),
})
    .strict();
export const startRecipeTranscriptionCommandSchema = startTranscriptionCommandSchema
    .omit({ append: true })
    .extend({ expectedRevision: z.number().int().positive() });
export const createMediaShareCommandSchema = z
    .object({
    assetId: ulidSchema,
    chapterId: ulidSchema.optional(),
})
    .strict();
export const printImagePlacementSchema = z
    .object({
    chapterId: ulidSchema,
    assetId: ulidSchema,
    placement: z.literal("CHAPTER_OPENER"),
    caption: z.string().trim().min(1).max(300).optional(),
})
    .strict();
export const printQrPlacementSchema = z
    .object({
    chapterId: ulidSchema,
    shareId: ulidSchema,
    label: z.string().trim().min(1).max(100).optional(),
})
    .strict();
export const renderPreviewCommandSchema = z
    .object({
    expectedRevision: z.number().int().nonnegative(),
    images: z.array(printImagePlacementSchema).max(24).default([]),
    qrShares: z.array(printQrPlacementSchema).max(24).default([]),
    fulfillmentTarget: z.literal("GELATO_LG_CANARY").optional(),
})
    .strict()
    .superRefine((command, context) => {
    for (const [field, placements] of [
        ["images", command.images],
        ["qrShares", command.qrShares],
    ]) {
        const chapterIds = new Set();
        placements.forEach((placement, index) => {
            if (chapterIds.has(placement.chapterId))
                context.addIssue({
                    code: "custom",
                    path: [field, index, "chapterId"],
                    message: `Only one ${field === "images" ? "image" : "QR share"} may be placed in a chapter.`,
                });
            chapterIds.add(placement.chapterId);
        });
    }
});
export const approveBookCommandSchema = z
    .object({ previewId: ulidSchema, expectedRevision: z.number().int().nonnegative() })
    .strict();
export const transcriptionJobResponseSchema = z.object({
    data: z.object({
        jobId: ulidSchema,
        state: z.enum(["QUEUED", "RUNNING", "SUCCEEDED", "FAILED", "NEEDS_ATTENTION"]),
        /**
         * A test book's or test account's job (D17, 2026-11-02): false for a real customer's. Pages
         * open before then never read it (they check only the job and its state), so a false is safe.
         */
        test: z.boolean(),
    }),
});
export const problemDetailsSchema = z.object({
    type: z.string().url(),
    title: z.string(),
    status: z.number().int().min(400).max(599),
    detail: z.string().optional(),
    instance: z.string().optional(),
    correlationId: z.string().uuid(),
});
//# sourceMappingURL=schemas.js.map